import { symRoundedImage, symRoundedPictureAsPdf } from '@quasar/extras/material-symbols-rounded'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import { computed, reactive, readonly, ref } from 'vue'
import type { FileTypeConfig, UploadableFile, UploadRequest } from '../types'
import { ProcessingPriority } from '../types'
import { useAiDocumentApi } from './useAiDocumentApi'

/**
 * File upload composable for AI document processing
 * Handles drag & drop, validation, progress tracking, and upload operations
 */
export function useFileUpload() {
  // API composable
  const api = useAiDocumentApi()

  // I18n composable
  const { t } = useCompositeI18n('core.AiDocumentProcessor')

  // Reactive state
  const isDragOver = ref(false)
  const uploadQueue = reactive<Map<string, UploadableFile>>(new Map())
  const uploadingFiles = ref<Set<string>>(new Set())

  // File type configurations
  const supportedFileTypes: FileTypeConfig[] = [
    {
      mimeType: 'application/pdf',
      extensions: ['.pdf'],
      icon: symRoundedPictureAsPdf,
      maxSize: 10 * 1024 * 1024, // 10MB (matches backend validation)
    },
    {
      mimeType: 'image/jpeg',
      extensions: ['.jpg', '.jpeg'],
      icon: symRoundedImage,
      maxSize: 10 * 1024 * 1024, // 10MB
    },
    {
      mimeType: 'image/png',
      extensions: ['.png'],
      icon: symRoundedImage,
      maxSize: 10 * 1024 * 1024, // 10MB
    },
    {
      mimeType: 'image/gif',
      extensions: ['.gif'],
      icon: symRoundedImage,
      maxSize: 10 * 1024 * 1024, // 10MB
    },
    {
      mimeType: 'image/webp',
      extensions: ['.webp'],
      icon: symRoundedImage,
      maxSize: 10 * 1024 * 1024, // 10MB
    },
    {
      mimeType: 'image/heic',
      extensions: ['.heic'],
      icon: symRoundedImage,
      maxSize: 10 * 1024 * 1024, // 10MB
    },
    {
      mimeType: 'image/heif',
      extensions: ['.heif'],
      icon: symRoundedImage,
      maxSize: 10 * 1024 * 1024, // 10MB
    },
  ]

  // Computed properties
  const queuedFiles = computed(() => Array.from(uploadQueue.values()))

  const hasFiles = computed(() => uploadQueue.size > 0)

  const isUploading = computed(() => uploadingFiles.value.size > 0)

  const uploadProgress = computed(() => {
    if (uploadQueue.size === 0) return 0
    const totalProgress = queuedFiles.value.reduce((sum, file) => sum + file.progress, 0)
    return Math.round(totalProgress / uploadQueue.size)
  })

  const supportedMimeTypes = computed(() => supportedFileTypes.map((config) => config.mimeType))

  const supportedExtensions = computed(() => supportedFileTypes.flatMap((config) => config.extensions))

  /**
   * Generate unique ID for files
   */
  const generateFileId = (): string => {
    return `file_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
  }

  /**
   * Get file type configuration
   */
  const getFileTypeConfig = (mimeType: string): FileTypeConfig | null => {
    return supportedFileTypes.find((config) => config.mimeType === mimeType) || null
  }

  /**
   * Validate file against supported types and size limits
   */
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const config = getFileTypeConfig(file.type)

    if (!config) {
      return {
        valid: false,
        error: t('errors.fileUpload.invalidType', {
          type: file.type,
          types: supportedMimeTypes.value.join(', '),
        }),
      }
    }

    if (file.size > config.maxSize) {
      const maxSizeMB = Math.round(config.maxSize / (1024 * 1024))
      return {
        valid: false,
        error: t('errors.fileUpload.sizeTooLarge', {
          maxSize: maxSizeMB,
          type: file.type,
        }),
      }
    }

    return { valid: true }
  }

  /**
   * Add files to upload queue with validation
   */
  const addFiles = (files: File[]): void => {
    files.forEach((file) => {
      const validation = validateFile(file)
      const fileId = generateFileId()

      const uploadableFile: UploadableFile = {
        file,
        id: fileId,
        status: validation.valid ? 'pending' : 'error',
        progress: 0,
        error: validation.error,
      }

      uploadQueue.set(fileId, uploadableFile)
    })
  }

  /**
   * Remove file from queue
   */
  const removeFile = (fileId: string): void => {
    uploadQueue.delete(fileId)
    uploadingFiles.value.delete(fileId)
  }

  /**
   * Clear all files from queue
   */
  const clearQueue = (): void => {
    uploadQueue.clear()
    uploadingFiles.value.clear()
  }

  /**
   * Clear completed and error files
   */
  const clearCompleted = (): void => {
    const completedFiles = queuedFiles.value.filter((file) => file.status === 'completed' || file.status === 'error')

    completedFiles.forEach((file) => removeFile(file.id))
  }

  /**
   * Update file progress
   */
  const updateFileProgress = (fileId: string, progress: number): void => {
    const file = uploadQueue.get(fileId)
    if (file) {
      file.progress = Math.min(100, Math.max(0, progress))
      uploadQueue.set(fileId, file)
    }
  }

  /**
   * Update file status
   */
  const updateFileStatus = (fileId: string, status: UploadableFile['status'], error?: string): void => {
    const file = uploadQueue.get(fileId)
    if (file) {
      file.status = status
      if (error) {
        file.error = error
      }
      uploadQueue.set(fileId, file)
    }
  }

  /**
   * Upload files with progress tracking
   */
  const uploadFiles = async (
    priority: ProcessingPriority = ProcessingPriority.Normal,
    targetModelClass?: string,
    modelConfigKey?: string,
    confirmedModelType?: string,
    autoProcess = true,
  ): Promise<string[]> => {
    const pendingFiles = queuedFiles.value.filter((file) => file.status === 'pending')

    if (pendingFiles.length === 0) {
      throw new Error(t('messages.noFilesSelected'))
    }

    const uploadedQueueIds: string[] = []

    // Upload files in batches to avoid overwhelming the server
    const BATCH_SIZE = 3
    const batches = []

    for (let i = 0; i < pendingFiles.length; i += BATCH_SIZE) {
      batches.push(pendingFiles.slice(i, i + BATCH_SIZE))
    }

    for (const batch of batches) {
      const uploadPromises = batch.map(async (uploadableFile) => {
        const fileId = uploadableFile.id

        try {
          // Mark as uploading
          uploadingFiles.value.add(fileId)
          updateFileStatus(fileId, 'uploading')
          updateFileProgress(fileId, 0)

          // Create upload request
          const uploadRequest: UploadRequest = {
            files: [uploadableFile.file],
            priority,
            target_model_class: targetModelClass,
            model_config_key: modelConfigKey,
            confirmed_model_type: confirmedModelType,
            auto_process: autoProcess,
          }

          // Simulate progress (since we don't have real progress tracking from the API)
          const progressInterval = setInterval(() => {
            const currentProgress = uploadQueue.get(fileId)?.progress || 0
            if (currentProgress < 90) {
              updateFileProgress(fileId, currentProgress + 10)
            }
          }, 200)

          // Upload file
          const response = await api.uploadAndProcess(uploadRequest)

          clearInterval(progressInterval)

          if (response) {
            updateFileProgress(fileId, 100)
            updateFileStatus(fileId, 'completed')
            uploadedQueueIds.push(response.queue_id)
          } else {
            throw new Error(api.error.value?.message || t('messages.uploadFailed'))
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : t('messages.uploadFailed')
          updateFileStatus(fileId, 'error', errorMessage)
        } finally {
          uploadingFiles.value.delete(fileId)
        }
      })

      // Wait for current batch to complete before starting next
      await Promise.all(uploadPromises)
    }

    return uploadedQueueIds
  }

  /**
   * Handle drag enter event
   */
  const handleDragEnter = (event: DragEvent): void => {
    event.preventDefault()
    isDragOver.value = true
  }

  /**
   * Handle drag leave event
   */
  const handleDragLeave = (event: DragEvent): void => {
    event.preventDefault()
    // Only set to false if leaving the drop zone completely
    if (!event.relatedTarget || !(event.currentTarget as Element).contains(event.relatedTarget as Node)) {
      isDragOver.value = false
    }
  }

  /**
   * Handle drag over event
   */
  const handleDragOver = (event: DragEvent): void => {
    event.preventDefault()
    event.dataTransfer!.dropEffect = 'copy'
  }

  /**
   * Handle file drop event
   */
  const handleDrop = (event: DragEvent): void => {
    event.preventDefault()
    isDragOver.value = false

    const files = Array.from(event.dataTransfer?.files || [])
    if (files.length > 0) {
      addFiles(files)
    }
  }

  /**
   * Handle file input change
   */
  const handleFileInput = (event: Event): void => {
    const input = event.target as HTMLInputElement
    const files = Array.from(input.files || [])

    if (files.length > 0) {
      addFiles(files)
      // Clear input for re-selection of same files
      input.value = ''
    }
  }

  /**
   * Retry failed upload
   */
  const retryUpload = async (fileId: string): Promise<void> => {
    const file = uploadQueue.get(fileId)
    if (file && file.status === 'error') {
      updateFileStatus(fileId, 'pending')
      updateFileProgress(fileId, 0)
      file.error = undefined
    }
  }

  // Return readonly state and actions
  return {
    // State (readonly)
    isDragOver: readonly(isDragOver),
    queuedFiles,
    hasFiles,
    isUploading,
    uploadProgress,
    supportedFileTypes,
    supportedMimeTypes,
    supportedExtensions,
    apiLoading: api.loading,
    apiError: api.error,

    // Actions
    addFiles,
    removeFile,
    clearQueue,
    clearCompleted,
    uploadFiles,
    retryUpload,
    validateFile,
    getFileTypeConfig,

    // Event handlers
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInput,

    // API actions
    clearApiError: api.clearError,
  } as const
}
